import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  WINE_LOOKUP_LABELS,
  type LookupAdminRow,
  type LookupMergeResult,
  type WineLookupKind,
} from '@gwc/shared';
import { lookupAdminApi } from '../../api/lookupAdmin';
import { winesApi } from '../../api/wines';
import { ApiError } from '../../lib/api';
import { Button, Dialog } from '../ui';
import { LookupPicker } from './LookupPicker';
import { describeRepointed, type PickedLookup } from './setupHelpers';

/**
 * Fold a duplicate into the entry that should survive. Everything pointing at
 * the duplicate moves to the survivor, the duplicate goes, and Cellar names made
 * from it are respelled. Afterwards it reports any Cellar wines that became
 * identical to another one, so they can be tidied by hand.
 */
export function MergeDialog({
  kind,
  row,
  initialInto,
  onClose,
  onMerged,
}: {
  kind: WineLookupKind;
  /** The duplicate; null = closed. */
  row: LookupAdminRow | null;
  initialInto?: PickedLookup | null;
  onClose: () => void;
  onMerged: (result: LookupMergeResult) => void;
}) {
  const [into, setInto] = useState<PickedLookup | null>(initialInto ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LookupMergeResult | null>(null);

  useEffect(() => {
    setInto(initialInto ?? null);
    setError(null);
    setResult(null);
  }, [row, initialInto]);

  const label = WINE_LOOKUP_LABELS[kind].one.toLowerCase();

  async function merge() {
    if (!row || !into) return;
    setBusy(true);
    setError(null);
    try {
      const res = await lookupAdminApi.merge(kind, row.id, into.id);
      setResult(res);
      onMerged(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not merge');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      title={result ? 'Merged' : `Merge ${row?.name ?? ''}`}
      description={
        result
          ? undefined
          : `Pick the ${label} to keep. ${row?.name ?? 'This entry'} is folded into it and removed.`
      }
      size="lg"
      footer={
        result ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="quiet" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={busy} disabled={!into} onClick={() => void merge()}>
              {into ? `Merge into ${into.name}` : 'Merge'}
            </Button>
          </>
        )
      }
    >
      {row && !result ? (
        <div className="space-y-4">
          <LookupPicker
            kind={kind}
            label={`Keep this ${label}`}
            value={into}
            onChange={setInto}
            excludeIds={[row.id]}
            allowClear={false}
            placeholder={`Search ${WINE_LOOKUP_LABELS[kind].many.toLowerCase()}…`}
          />
          <ul className="list-disc space-y-1 pl-5 text-sm text-cream-300/80">
            <li>
              Everything using {row.name} ({row.usageCount} {row.usageCount === 1 ? 'use' : 'uses'}) will use{' '}
              {into ? into.name : 'the one you keep'} instead.
            </li>
            {kind === 'regions' ? (
              <li>Its sub-regions and vineyards move across; any that already exist there are merged too.</li>
            ) : null}
            <li>Cellar wines named after it are renamed, and RSVPs and tasting pours show the new spelling.</li>
            <li>This can't be undone.</li>
          </ul>
          {error ? (
            <p className="rounded-md border border-danger/40 px-3 py-2 text-sm text-danger" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
      {result ? (
        <div className="space-y-4 text-sm">
          <p className="text-cream-100">
            {row?.name} is now part of <span className="text-gold-200">{result.into.name}</span>.
          </p>
          <p className="text-cream-300/70">Updated: {describeRepointed(result.repointed)}.</p>
          {result.catalogConflicts.length ? (
            <div className="rounded-md border border-warning/40 px-3 py-3" role="status">
              <p className="text-warning">
                {result.catalogConflicts.length === 1
                  ? '1 Cellar wine now matches another entry exactly.'
                  : `${result.catalogConflicts.length} Cellar wines now match another entry exactly.`}
              </p>
              <p className="mt-1 text-cream-300/70">
                They were left as they were and flagged for review. Open each one and tidy up the duplicate.
              </p>
              <ul className="mt-2 space-y-1">
                {result.catalogConflicts.map((id) => (
                  <li key={id}>
                    <ConflictLink id={id} onOpen={onClose} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}

/** "2019 Ridge Monte Bello" → the Cellar page, so the admin can resolve the twin. */
function ConflictLink({ id, onOpen }: { id: string; onOpen: () => void }) {
  const { data } = useQuery({ queryKey: ['wine', id], queryFn: () => winesApi.get(id), staleTime: 60_000 });
  const w = data?.wine;
  const label = w
    ? [w.vintage ?? 'NV', w.producer, w.wineName].filter(Boolean).join(' ')
    : 'Open in the Cellar';
  return (
    <Link to={`/cellar/${id}`} className="text-gold-300 underline hover:text-gold-200" onClick={onOpen}>
      {label}
    </Link>
  );
}
